import type React from 'react';

import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Box, type SxProps, Typography } from '@mui/material';
import Button from '@mui/material/Button';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useNotificationsContext } from '@/contexts/notifications/Notifications.context.ts';
import { FeedbackModal } from '@/layouts/private/topBar/components/FeedbackDialog.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

interface SideBarGetSupportProps {
  lightBackground?: boolean;
  showExpandedContent: boolean;
}

export const SideBarGetSupport: React.FC<SideBarGetSupportProps> = ({
  lightBackground = false,
  showExpandedContent,
}) => {
  const { closeFeedbackDialog, isFeedbackDialogVisible, openFeedbackDialog } =
    useNotificationsContext();
  const { t } = useTranslationWithPrefix('layouts.sideBar.support.button');

  return (
    <>
      <Button onClick={openFeedbackDialog} sx={getStyles(showExpandedContent, lightBackground)}>
        <SupportAgentIcon />
        <Box
          sx={{
            opacity: showExpandedContent ? 1 : 0,
            overflow: 'hidden',
            transform: showExpandedContent ? 'translateX(0)' : 'translateX(-5px)',
            transition: showExpandedContent
              ? 'opacity 0.3s ease-out 0.1s, transform 0.3s ease-out 0.1s'
              : 'opacity 0.2s ease-in, transform 0.2s ease-in',
            width: showExpandedContent ? 'auto' : 0,
          }}
        >
          <Typography sx={{ whiteSpace: 'nowrap' }} variant="f14Regular">
            {t('feedback')}
          </Typography>
        </Box>
      </Button>
      {isFeedbackDialogVisible && <FeedbackModal onClose={closeFeedbackDialog} />}
    </>
  );
};

const getStyles = (showExpandedContent: boolean, lightBackground: boolean): SxProps => ({
  '&:hover': {
    backgroundColor: lightBackground ? themeColors.silver_40 : themeColors.silver_20,
    cursor: 'pointer',
  },
  alignItems: 'center',
  background: 'transparent',
  border: `1px solid ${lightBackground ? themeColors.grey : themeColors.stroke}`,
  borderRadius: '50px',
  color: lightBackground ? themeColors.darkGrey : 'inherit',
  display: 'flex',
  gap: showExpandedContent ? 1 : 0,
  height: '40px',
  justifyContent: 'center',
  minWidth: '40px',
  mx: showExpandedContent ? 2 : 1,
  p: 1,
  transition: 'all 0.2s ease-in-out',
  width: showExpandedContent ? 'auto' : '40px',
});
